// Sopwith 3D - shots, bombs, missiles, starbursts, explosions, smoke, craters.
//
// Copyright (C) 1984-2000 David L. Clark
// Copyright (C) 2001-2026 Simon Howard and SDL Sopwith contributors
// Copyright (C) 2026 Sopwith 3D contributors
//
// This program is free software; you can redistribute it and/or modify it
// under the terms of the GNU General Public License as published by the Free
// Software Foundation; either version 2 of the License, or (at your option)
// any later version. This program is distributed WITHOUT ANY WARRANTY.
//
// Ported from SDL Sopwith swinit.c (creation) and swmove.c (movement).

import { aim } from './ai';
import { collisionTest } from './collision';
import {
  ANGLES,
  BOMBLIFE,
  BULLIFE,
  BULSPEED,
  BURSTLIFE,
  EXPLLIFE,
  MAX_Y,
  MISSLIFE,
  SMOKELIFE,
} from './constants';
import { clampRange, COS, iabs, imax, inRange, movexy, setdxdy, SIN } from './fixed';
import type { Game } from './game';
import { copyobj } from './objects';
import { planeIsWounded, symangle } from './plane';
import { getSymset, SYMBOL_PIXEL } from './symbols';
import { Obj, ObjState, ObjType, PlayMode, PowerupType, TargetType } from './types';

const xy = { x: 0, y: 0 };
const scratchA = new Obj();
const scratchB = new Obj();

// ---------------------------------------------------------------------------
// Shots

/** Approximate distance for aiming ground fire; -1 when out of range. */
function isrange(x: number, y: number, ax: number, ay: number): number {
  let dy = iabs(y - ay);
  dy += dy >> 1;
  let dx = iabs(x - ax);
  if (dx > 100 || dy > 100) {
    return -1;
  }
  if (dx < dy) {
    const t = dx;
    dx = dy;
    dy = t;
  }
  return ((7 * dx + 4 * dy) / 8) | 0;
}

/**
 * Nudge a freshly spawned bullet until its next position is clear of the
 * object that fired it, so buildings cannot shoot themselves.
 */
function adjustBullet(g: Game, bullet: Obj, src: Obj): void {
  const nextSrc = scratchA;
  const nextBullet = scratchB;
  copyobj(nextSrc, src);
  movexy(nextSrc, g.level.maxX, xy);
  for (let guard = 0; guard < 64; guard++) {
    copyobj(nextBullet, bullet);
    movexy(nextBullet, g.level.maxX, xy);
    if (!collisionTest(nextBullet, nextSrc)) {
      return;
    }
    bullet.x += bullet.dx < 0 ? -2 : 2;
    bullet.y += bullet.dy < 0 ? -2 : 2;
  }
}

/** Fire the gun of `obo`; with `targ`, a ground object shooting at a plane. */
export function initshot(g: Game, obo: Obj, targ: Obj | null): void {
  if (!targ && !g.compplane && !obo.rounds) {
    return;
  }
  const ob = g.world.allocobj();
  if (g.playmode !== PlayMode.NOVICE) {
    obo.rounds--;
  }
  const bspeed = BULSPEED + g.gamenum;

  if (targ) {
    const x = targ.x + (targ.dx << 2);
    const y = targ.y + (targ.dy << 2);
    const dx = x - obo.x;
    const dy = y - obo.y;
    const r = isrange(x, y, obo.x, obo.y);
    if (r < 1) {
      g.world.deallobj(ob);
      return;
    }
    ob.dx = ((dx * bspeed) / r) | 0;
    ob.dy = ((dy * bspeed) / r) | 0;
    ob.ldx = ob.ldy = 0;
  } else {
    const nspeed = obo.speed + bspeed;
    setdxdy(ob, nspeed * COS(obo.angle), nspeed * SIN(obo.angle));
  }

  ob.type = ObjType.SHOT;
  ob.x = obo.x + ((obo.symbol!.w / 2) | 0);
  ob.y = obo.y - ((obo.symbol!.h / 2) | 0);
  ob.lx = obo.lx;
  ob.ly = obo.ly;
  ob.life = BULLIFE;
  ob.owner = obo;
  ob.clr = obo.clr;
  ob.symbol = SYMBOL_PIXEL;
  ob.movef = moveshot;
  ob.speed = 0;
  if (obo.type === ObjType.BALLOON) {
    ob.y -= 4; // fired from the basket
  }
  adjustBullet(g, ob, obo);
  g.world.insertx(ob, obo);
}

export function moveshot(g: Game, ob: Obj): boolean {
  ob.life--;
  if (ob.life <= 0) {
    g.world.deallobj(ob);
    return false;
  }
  g.world.movexy(ob, g.level.maxX, xy);
  if (!inRange(0, xy.x, g.level.maxX - 1) || !inRange(g.ground[xy.x] + 1, xy.y, MAX_Y - 1)) {
    g.world.deallobj(ob);
    return false;
  }
  ob.symbol = SYMBOL_PIXEL;
  return true;
}

// ---------------------------------------------------------------------------
// Bombs

/** Gravity for bombs and debris: accelerate downward every few ticks. */
export function adjustfall(ob: Obj): void {
  ob.life--;
  if (ob.life <= 0) {
    if (ob.dy < 0) {
      if (ob.dx < 0) {
        ob.dx++;
      } else if (ob.dx > 0) {
        ob.dx--;
      }
    }
    if (ob.dy > -10) {
      ob.dy--;
    }
    ob.life = BOMBLIFE;
  }
}

export function initbomb(g: Game, obo: Obj): void {
  if ((!g.compplane && !obo.bombs) || obo.bdelay) {
    return;
  }
  const ob = g.world.allocobj();
  if (g.playmode !== PlayMode.NOVICE) {
    obo.bombs--;
  }
  obo.bdelay = 10;

  ob.type = ObjType.BOMB;
  ob.state = ObjState.FALLING;
  ob.dx = obo.dx;
  ob.dy = obo.dy;
  ob.onmap = true;

  // Released from the underside of the plane.
  const angle = obo.orient
    ? (obo.angle + ANGLES / 4) % ANGLES
    : (obo.angle + (3 * ANGLES) / 4) % ANGLES;
  ob.x = obo.x + ((COS(angle) * 10) >> 8) + 4;
  ob.y = obo.y + ((SIN(angle) * 10) >> 8) - 4;
  ob.lx = ob.ly = ob.ldx = ob.ldy = 0;

  ob.life = BOMBLIFE;
  ob.owner = obo;
  ob.clr = obo.clr;
  ob.symbol = getSymset(g.symbols, 'swbmbsym', 0).sym[0];
  ob.movef = movebomb;
  g.world.insertx(ob, obo);
}

export function movebomb(g: Game, ob: Obj): boolean {
  if (ob.life < 0) {
    g.world.deallobj(ob);
    ob.state = ObjState.FINISHED;
    return false;
  }
  adjustfall(ob);
  g.world.movexy(ob, g.level.maxX, xy);
  if (!inRange(0, xy.x, g.level.maxX - 1)) {
    g.world.deallobj(ob);
    ob.state = ObjState.FINISHED;
    return false;
  }
  const ang = symangle(ob);
  ob.symbol = getSymset(g.symbols, 'swbmbsym', ang % 2).sym[(ang / 2) | 0];
  return xy.y < MAX_Y;
}

// ---------------------------------------------------------------------------
// Missiles and starbursts (the optional `-x` weapons)

export function initmiss(g: Game, obo: Obj): void {
  if (obo.mdelay || !obo.missiles || !g.conf.missiles) {
    return;
  }
  const ob = g.world.allocobj();
  if (g.playmode !== PlayMode.NOVICE) {
    obo.missiles--;
  }
  obo.mdelay = 5;

  ob.type = ObjType.MISSILE;
  ob.state = ObjState.FLYING;
  const angle = (ob.angle = obo.angle);
  ob.x = obo.x + (COS(angle) >> 4) + 4;
  ob.y = obo.y + (SIN(angle) >> 4) - 4;
  ob.lx = ob.ly = 0;
  const nspeed = (ob.speed = g.gmaxspeed + (g.gmaxspeed >> 1));
  setdxdy(ob, nspeed * COS(angle), nspeed * SIN(angle));
  ob.life = MISSLIFE;
  ob.owner = obo;
  ob.clr = obo.clr;
  ob.symbol = getSymset(g.symbols, 'swmscsym', 0).sym[0];
  ob.movef = movemiss;
  ob.missiletarget = obo.mfiring;
  ob.orient = ob.accel = ob.flaps = 0;
  ob.onmap = true;
  g.world.insertx(ob, obo);
}

export function movemiss(g: Game, ob: Obj): boolean {
  if (ob.life < 0) {
    g.world.deallobj(ob);
    ob.state = ObjState.FINISHED;
    return false;
  }
  if (ob.state === ObjState.FLYING) {
    let obt = ob.missiletarget;
    if (obt && obt !== ob.owner && ob.life & 1) {
      if (obt.missiletarget) {
        obt = obt.missiletarget; // decoyed by a starburst
      }
      aim(g, ob, obt.x, obt.y, null, false);
      const angle = (ob.angle = (ob.angle + ob.flaps + ANGLES) % ANGLES);
      setdxdy(ob, ob.speed * COS(angle), ob.speed * SIN(angle));
    }
    g.world.movexy(ob, g.level.maxX, xy);
    ob.life--;
    if (ob.life <= 0 || xy.y >= (MAX_Y * 3) / 2) {
      ob.state = ObjState.FALLING;
      ob.life++;
    }
  } else {
    adjustfall(ob);
    ob.angle = (ob.angle + 1) % ANGLES;
    g.world.movexy(ob, g.level.maxX, xy);
  }
  if (!inRange(0, xy.x, g.level.maxX - 1)) {
    g.world.deallobj(ob);
    ob.state = ObjState.FINISHED;
    return false;
  }
  ob.symbol = getSymset(g.symbols, 'swmscsym', ob.angle % 4).sym[(ob.angle / 4) | 0];
  return xy.y < MAX_Y;
}

export function initburst(g: Game, obo: Obj): void {
  if (obo.bsdelay || !obo.bursts || !g.conf.missiles) {
    return;
  }
  const ob = g.world.allocobj();
  obo.bsdelay = 5;
  if (g.playmode !== PlayMode.NOVICE) {
    obo.bursts--;
  }
  ob.type = ObjType.STARBURST;
  ob.state = ObjState.FALLING;
  const angle = obo.orient
    ? (obo.angle + (3 * ANGLES) / 8) % ANGLES
    : (obo.angle + (5 * ANGLES) / 8) % ANGLES;
  setdxdy(ob, g.gminspeed * COS(angle), g.gminspeed * SIN(angle));
  ob.dx += obo.dx;
  ob.dy += obo.dy;
  ob.x = obo.x + ((COS(angle) * 10) >> 10) + 4;
  ob.y = obo.y + ((SIN(angle) * 10) >> 10) - 4;
  ob.lx = ob.ly = 0;
  ob.life = BURSTLIFE;
  ob.owner = obo;
  ob.clr = obo.clr;
  ob.symbol = getSymset(g.symbols, 'swbstsym', 0).sym[0];
  ob.movef = moveburst;
  g.world.insertx(ob, obo);
}

export function moveburst(g: Game, ob: Obj): boolean {
  if (ob.life < 0) {
    ob.owner!.missiletarget = null;
    g.world.deallobj(ob);
    return false;
  }
  adjustfall(ob);
  g.world.movexy(ob, g.level.maxX, xy);
  if (!inRange(0, xy.x, g.level.maxX - 1) || xy.y <= g.ground[xy.x]) {
    ob.owner!.missiletarget = null;
    g.world.deallobj(ob);
    return false;
  }
  ob.owner!.missiletarget = ob;
  ob.symbol = getSymset(g.symbols, 'swbstsym', ob.life & 1).sym[0];
  return xy.y < MAX_Y;
}

// ---------------------------------------------------------------------------
// Explosions and smoke

function targetExplosionSize(g: Game, targetType: number): number {
  const s = getSymset(g.symbols, 'swhtrsym', targetType).sym[0];
  let n = 0;
  for (let i = 0; i < s.data.length; i++) {
    if (s.data[i]) n++;
  }
  return clampRange(10, n, 64);
}

/** Larger-than-16x16 sprites explode across their whole extent. */
function applySpriteSizeOffset(ob: Obj, obo: Obj, angle: number): void {
  const rangex = imax(obo.symbol!.w, 16) - 16;
  const rangey = imax(obo.symbol!.h, 16) - 16;
  ob.x += ((COS(angle) * rangex) / (256 * 2)) | 0;
  ob.y += ((SIN(angle) * rangey) / (256 * 2)) | 0;
  ob.y -= rangey;
}

/** Fuel tanks and fuel power-ups go up with a bang. */
function isExplosive(ob: Obj): boolean {
  switch (ob.type) {
    case ObjType.TARGET:
      return (
        ob.orient === TargetType.OIL_TANK ||
        ob.orient === TargetType.TANKER_TRUCK ||
        ob.orient === TargetType.CUSTOM5 ||
        ob.orient === TargetType.CUSTOM_PASSIVE5
      );
    case ObjType.POWERUP:
      return ob.orient === PowerupType.FUEL || ob.orient === PowerupType.FUEL_BIG;
    default:
      return false;
  }
}

/** Blow `obo` up: a ring of debris fragments, fewer when `small`. */
export function initexpl(g: Game, obo: Obj, small: number): void {
  const sym = obo.symbol!;
  const obox = obo.x + ((sym.w / 2) | 0);
  const oboy = obo.y + ((sym.h / 2) | 0);
  const obodx = obo.dx >> 2;
  const obody = obo.dy >> 2;
  const oboclr = obo.clr;
  const obotype = obo.type;

  let ic: number;
  let speed: number;
  if (isExplosive(obo)) {
    ic = 1;
    speed = g.conf.bigExplosions ? g.gminspeed + 4 : g.gminspeed;
  } else {
    speed = g.gminspeed >> ((g.explseed & 7) !== 7 ? 1 : 0);
    if (small) {
      ic = 6;
    } else if (obotype === ObjType.TARGET) {
      ic = (110 / targetExplosionSize(g, obo.orient)) | 0;
    } else {
      ic = 2;
    }
  }
  // A flying plane throws out its pilot as one of the fragments.
  let mansym =
    obotype === ObjType.PLANE && (obo.state === ObjState.FLYING || obo.state === ObjState.WOUNDED);

  for (let i = 1; i <= 15; i += ic) {
    const ob = g.world.allocobj();
    ob.type = ObjType.EXPLOSION;
    setdxdy(ob, COS(i) * speed, SIN(i) * speed);
    ob.dx += obodx;
    ob.dy += obody;
    ob.x = obox + ob.dx;
    ob.y = oboy + ob.dy;
    applySpriteSizeOffset(ob, obo, i);

    // The original's home-grown random generator, kept for feel.
    g.explseed = Math.imul(g.explseed, Math.imul(ob.x, ob.y)) >>> 0;
    g.explseed = (g.explseed + 7491) >>> 0;
    if (!g.explseed) {
      g.explseed = 74917777;
    }

    ob.life = EXPLLIFE;
    let orient = (ob.orient = (g.explseed & 0x01c0) >> 6);
    if (mansym && (!orient || orient === 7)) {
      orient = ob.orient = 0;
      mansym = false;
      ob.dx = obodx;
      ob.dy = -g.gminspeed;
    }
    ob.lx = ob.ly = ob.hitcount = ob.speed = 0;
    ob.owner = obo;
    ob.clr = oboclr;
    ob.symbol = getSymset(g.symbols, 'swexpsym', 0).sym[0];
    ob.movef = moveexpl;
    g.world.insertx(ob, obo);
  }
}

export function moveexpl(g: Game, ob: Obj): boolean {
  if (ob.life < 0) {
    g.world.deallobj(ob);
    return false;
  }
  ob.life--;
  if (ob.life <= 0) {
    if (ob.dy < 0) {
      if (ob.dx < 0) {
        ob.dx++;
      } else if (ob.dx > 0) {
        ob.dx--;
      }
    }
    if ((ob.orient && ob.dy > -10) || (!ob.orient && ob.dy > -g.gminspeed)) {
      ob.dy--;
    }
    ob.life = EXPLLIFE;
  }
  g.world.movexy(ob, g.level.maxX, xy);
  if (!inRange(0, xy.x, g.level.maxX - 1) || xy.y <= g.ground[xy.x]) {
    g.world.deallobj(ob);
    return false;
  }
  ob.hitcount++;
  ob.symbol = getSymset(g.symbols, 'swexpsym', ob.orient).sym[0];
  return xy.y < MAX_Y;
}

/** A puff of smoke trailing a falling or wounded plane. */
export function initsmok(g: Game, obo: Obj): void {
  const ob = g.world.allocobj();
  ob.type = ObjType.SMOKE;
  ob.x = obo.x + 8;
  ob.y = obo.y - 8;
  ob.dx = obo.dx;
  ob.dy = obo.dy;
  ob.lx = ob.ly = ob.ldx = ob.ldy = 0;
  ob.life = SMOKELIFE;
  ob.owner = obo;
  ob.movef = movesmok;
  ob.clr = obo.clr;
  ob.symbol = SYMBOL_PIXEL;
}

export function movesmok(g: Game, ob: Obj): boolean {
  const planestate = ob.owner!.state;
  ob.life--;
  if (
    ob.life <= 0 ||
    (planestate !== ObjState.FALLING &&
      planestate !== ObjState.CRASHED &&
      !planeIsWounded(planestate))
  ) {
    g.world.deallobj(ob);
    return false;
  }
  ob.symbol = SYMBOL_PIXEL;
  return true;
}

// ---------------------------------------------------------------------------
// Craters

const CRTDEPTH = [1, 2, 2, 3, 3, 2, 2, 1];

/** Dig an eight-column crater under `ob`, never deeper than 20 below the level's ground. */
export function crater(g: Game, ob: Obj): void {
  const xmin = ob.x + (((ob.symbol!.w - 8) / 2) | 0);
  const xmax = xmin + 7;
  for (let x = xmin, i = 0; x <= xmax; x++, i++) {
    if (x < 0 || x >= g.level.maxX) continue;
    const ymax = g.ground[x];
    let ymin = ymax - CRTDEPTH[i] + 1;
    const y = imax(20, g.level.ground[x] - 20);
    if (ymin <= y) {
      ymin = y + 1;
    }
    g.ground[x] = ymin - 1;
  }
}
